import { useLayoutEffect, useMemo, useRef, useState, MouseEvent } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Fullscreen, FullscreenExit } from "@mui/icons-material";
import { addWeeks, addDays, format, isSameDay, isWithinInterval, parseISO, startOfWeek } from "date-fns";
import { Link as RouterLink } from "react-router-dom";
import { LogisticsTicket } from "types/logisticsTicket";
import useLogisticsTickets from "hooks/useLogisticsTickets";
import { matchesCoordinatorFilter, matchesVendorFilter } from "../matchesCoordinatorFilter";

type Props = {
  search?: string;
  coordinatorFilter?: string;
  vendorFilter?: string;
};

const WEEK_STARTS_ON = 1;
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 18;
const HOUR_HEIGHT = 76;
const MIN_HOUR_HEIGHT = 48;
const TIME_COL_WIDTH = 72;
const CONFIRMED_STAGES = new Set(["scheduled_confirmed", "picked_up", "delivery_verified"]);
const MAX_VISIBLE_CARDS_PER_HOUR = 2;

const EVENT_STYLES = {
  pending: {
    backgroundColor: "#FFF4C7",
    borderColor: "#F5B800",
    titleColor: "#7A5300",
  },
  confirmed: {
    backgroundColor: "#DDF9EA",
    borderColor: "#07834A",
    titleColor: "#05603A",
  },
};

function isPending(logisticsTicket: LogisticsTicket) {
  return logisticsTicket.state === "pending_confirmation";
}

function eventStyle(logisticsTicket: LogisticsTicket) {
  return isPending(logisticsTicket) ? EVENT_STYLES.pending : EVENT_STYLES.confirmed;
}

function getLogisticsTicketDate(logisticsTicket: LogisticsTicket): Date | null {
  const dateStr = isPending(logisticsTicket)
    ? logisticsTicket.winningQuote?.proposedDate
    : logisticsTicket.pickup.scheduledAt?.split("T")?.[0];
  if (!dateStr) return null;
  const parsed = parseISO(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseStartHour(timeWindow?: string): number {
  if (!timeWindow) return 9;
  const match = timeWindow.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (!match) return 9;

  let hour = Number(match[1]);
  const meridiem = match[3].toLowerCase();
  if (meridiem === "pm" && hour !== 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  return hour;
}

function formatHourLabel(hour: number) {
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  return format(date, "h a");
}

function ticketStartHour(logisticsTicket: LogisticsTicket) {
  const { pickup, winningQuote } = logisticsTicket;
  if (isPending(logisticsTicket)) {
    return parseStartHour(winningQuote?.proposedTime || winningQuote?.proposedTimeWindow);
  }
  return parseStartHour(pickup.confirmedTime || pickup.requestedTime);
}

function groupTicketsByHour(tickets: LogisticsTicket[]) {
  const groups = new Map<number, LogisticsTicket[]>();
  tickets.forEach((ticket) => {
    const hour = ticketStartHour(ticket);
    const existing = groups.get(hour) ?? [];
    existing.push(ticket);
    groups.set(hour, existing);
  });

  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([hour, hourTickets]) => ({ hour, tickets: hourTickets }));
}

function CalendarEventCard({ logisticsTicket }: { logisticsTicket: LogisticsTicket }) {
  const style = eventStyle(logisticsTicket);

  return (
    <Box
      component={RouterLink}
      to={`/logistics_platform/pickups/${logisticsTicket.uuid}`}
      sx={{
        flex: 1,
        minWidth: 0,
        height: "100%",
        textDecoration: "none",
        backgroundColor: style.backgroundColor,
        borderLeft: `4px solid ${style.borderColor}`,
        borderRadius: 1.5,
        px: 1,
        py: 0.75,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
        "&:hover": { filter: "brightness(0.98)" },
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 550,
          color: style.titleColor,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {logisticsTicket.pickup.organization.name}
      </Typography>
      <Typography sx={{ fontSize: 11, lineHeight: 1.3, color: style.titleColor, mt: 0.25 }} noWrap>
        PU-{logisticsTicket.pickup.orderNumber}
        {logisticsTicket.pickup.location?.isInternational && " · Intl"}
      </Typography>
      {logisticsTicket.winningQuote?.vendor && (
        <Typography sx={{ fontSize: 11, lineHeight: 1.3, color: style.titleColor, mt: 0.25 }} noWrap>
          {logisticsTicket.winningQuote.vendor.name}
        </Typography>
      )}
    </Box>
  );
}

function HourOverflowMenu({ tickets }: { tickets: LogisticsTicket[] }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={`${tickets.length} more pickups`}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        sx={{
          flex: "0 0 auto",
          height: "100%",
          border: "none",
          cursor: "pointer",
          backgroundColor: "#EEF2F6",
          color: "text.secondary",
          borderRadius: 1.5,
          fontSize: 11,
          fontWeight: 700,
          px: 0.75,
          whiteSpace: "nowrap",
        }}
      >
        +{tickets.length} more
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {tickets.map((ticket) => (
          <MenuItem
            key={ticket.id}
            component={RouterLink}
            to={`/logistics_platform/pickups/${ticket.uuid}`}
            onClick={() => setAnchorEl(null)}
            sx={{ borderLeft: `4px solid ${eventStyle(ticket).borderColor}` }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {ticket.pickup.organization.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PU-{ticket.pickup.orderNumber}
                {ticket.winningQuote?.vendor && ` · ${ticket.winningQuote.vendor.name}`}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function LogisticsTicketsCalendarView({
  search,
  coordinatorFilter = "all",
  vendorFilter = "all",
}: Props) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON }));

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const weekEnd = weekDays[6];
  const weekRange = (column: string) => [
    `${column},on_or_after,${format(weekStart, "yyyy-MM-dd")}`,
    `${column},on_or_before,${format(weekEnd, "yyyy-MM-dd")}`,
  ];
  const { logisticsTickets: scheduledTickets, isLoading: isLoadingScheduled } = useLogisticsTickets({
    search,
    pageSize: 500,
    filters: weekRange("pickups.scheduled_at"),
  });
  const { logisticsTickets: pendingTickets, isLoading: isLoadingPending } = useLogisticsTickets({
    search,
    pageSize: 500,
    filters: ["state,is,pending_confirmation", ...weekRange("logistics_ticket_quotes.proposed_date")],
  });
  const isLoading = isLoadingScheduled || isLoadingPending;

  const weekLogisticsTickets = useMemo(
    () =>
      [...scheduledTickets.filter((ticket) => CONFIRMED_STAGES.has(ticket.state)), ...pendingTickets].filter(
        (ticket) => {
          const date = getLogisticsTicketDate(ticket);
          return (
            ticket.winningQuote &&
            date &&
            isWithinInterval(date, { start: weekStart, end: weekEnd }) &&
            matchesCoordinatorFilter(ticket, coordinatorFilter) &&
            matchesVendorFilter(ticket, vendorFilter)
          );
        },
      ),
    [coordinatorFilter, pendingTickets, scheduledTickets, vendorFilter, weekEnd, weekStart],
  );

  const hourRange = useMemo(() => {
    const eventHours = weekLogisticsTickets.map(ticketStartHour);
    if (eventHours.length === 0) {
      return { start: DEFAULT_START_HOUR, end: DEFAULT_END_HOUR };
    }
    const minEventHour = eventHours.reduce((min, hour) => Math.min(min, hour), DEFAULT_START_HOUR);
    const maxEventHour = eventHours.reduce((max, hour) => Math.max(max, hour), DEFAULT_START_HOUR);
    return {
      start: Math.min(DEFAULT_START_HOUR, minEventHour),
      end: Math.max(DEFAULT_END_HOUR, maxEventHour + 1),
    };
  }, [weekLogisticsTickets]);

  const hours = useMemo(
    () => Array.from({ length: hourRange.end - hourRange.start }, (_, index) => hourRange.start + index),
    [hourRange],
  );

  const logisticsTicketsByDay = useMemo(() => {
    return weekDays.map((day) =>
      groupTicketsByHour(
        weekLogisticsTickets.filter((logisticsTicket) => {
          const date = getLogisticsTicketDate(logisticsTicket);
          return date ? isSameDay(date, day) : false;
        }),
      ),
    );
  }, [weekDays, weekLogisticsTickets]);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fitHourHeight, setFitHourHeight] = useState(HOUR_HEIGHT);
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const hourHeight = isFullScreen ? fitHourHeight : HOUR_HEIGHT;

  useLayoutEffect(() => {
    if (!isFullScreen) return undefined;

    const fitHours = () => {
      const available = (rootRef.current?.clientHeight ?? 0) - (headerRef.current?.offsetHeight ?? 0) - 1;
      setFitHourHeight(Math.max(MIN_HOUR_HEIGHT, Math.floor(available / hours.length)));
    };
    const exitOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsFullScreen(false);
    };

    fitHours();
    window.addEventListener("resize", fitHours);
    window.addEventListener("keydown", exitOnEscape);
    return () => {
      window.removeEventListener("resize", fitHours);
      window.removeEventListener("keydown", exitOnEscape);
    };
  }, [hours.length, isFullScreen]);

  const goToToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON }));

  return (
    <Box
      ref={rootRef}
      sx={
        isFullScreen
          ? {
              position: "fixed",
              inset: 0,
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: "background.paper",
              overflow: "auto",
              overscrollBehavior: "contain",
            }
          : { height: "100%", overflow: "auto", m: 0, p: 0 }
      }
    >
      <Box>
        <Box ref={headerRef} sx={{ position: "sticky", top: 0, zIndex: 2, backgroundColor: "background.paper" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 3,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}>
              Week of {format(weekStart, "MMMM d, yyyy")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
              Showing{" "}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {weekLogisticsTickets.length}
              </Typography>{" "}
              {weekLogisticsTickets.length === 1 ? "pickup" : "pickups"}
            </Typography>

            <ButtonGroup
              variant="outlined"
              size="small"
              sx={{ "& .MuiButton-root": { textTransform: "none", px: 1.5 } }}
            >
              <IconButton
                size="small"
                onClick={() => setWeekStart(addWeeks(weekStart, -1))}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: "6px 0 0 6px" }}
                aria-label="Previous week"
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Button onClick={goToToday} sx={{ borderRadius: 0, borderColor: "divider", color: "text.secondary" }}>
                Today
              </Button>
              <IconButton
                size="small"
                onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: "0 6px 6px 0" }}
                aria-label="Next week"
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </ButtonGroup>
            <Tooltip title={isFullScreen ? "Exit full screen (Esc)" : "Full screen"}>
              <IconButton
                size="small"
                onClick={() => setIsFullScreen(!isFullScreen)}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: "6px" }}
                aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
              >
                {isFullScreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))`,
              border: "1px solid",
              borderBottom: "none",
              borderColor: "divider",
            }}
          >
            <Box sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.100" }} />
            {weekDays.map((day) => {
              const isToday = isSameDay(day, new Date());
              return (
                <Box
                  key={day.toISOString()}
                  sx={{
                    py: 2,
                    px: 1.5,
                    textAlign: "left",
                    borderBottom: "1px solid",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    backgroundColor: isToday ? "#E1F5FE" : "grey.100",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: 0.4,
                      color: "text.secondary",
                      lineHeight: 1.3,
                    }}
                  >
                    {format(day, "EEE").toUpperCase()}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: isToday ? "#1D64C8" : "text.primary",
                      lineHeight: 1.2,
                      mt: 1,
                    }}
                  >
                    {format(day, "d")}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ border: "1px solid", borderTop: "none", borderColor: "divider", overflow: "clip" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))` }}>
              <Box>
                {hours.map((hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: hourHeight,
                      px: 1,
                      pt: 0.75,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "grey.100",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{formatHourLabel(hour)}</Typography>
                  </Box>
                ))}
              </Box>

              {weekDays.map((day, dayIndex) => {
                return (
                  <Box
                    key={day.toISOString()}
                    sx={{
                      position: "relative",
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "#fff",
                      backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${
                        hourHeight - 1
                      }px, #ECECEC ${hourHeight - 1}px, #ECECEC ${hourHeight}px)`,
                      minHeight: hours.length * hourHeight,
                    }}
                  >
                    {logisticsTicketsByDay[dayIndex].map(({ hour, tickets }) => {
                      const visibleTickets = tickets.slice(0, MAX_VISIBLE_CARDS_PER_HOUR);
                      const overflowTickets = tickets.slice(MAX_VISIBLE_CARDS_PER_HOUR);
                      const top = (hour - hourRange.start) * hourHeight;

                      return (
                        <Box
                          key={hour}
                          sx={{
                            position: "absolute",
                            left: 6,
                            right: 6,
                            top,
                            height: hourHeight - 12,
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          {visibleTickets.map((logisticsTicket) => (
                            <CalendarEventCard key={logisticsTicket.id} logisticsTicket={logisticsTicket} />
                          ))}
                          {overflowTickets.length > 0 && <HourOverflowMenu tickets={overflowTickets} />}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
